import type { ReactNode } from 'react';
import { Spinner } from './Spinner';

export interface LoginPageProps {
  /** Main title (e.g., "CRM אלקה") */
  title: string;
  /** Subtitle text (e.g., "ניהול קשרי לקוחות חכם ומתקדם") */
  subtitle: string;
  /** Called when Google sign-in button is clicked. When omitted, the Google button is hidden and only children are rendered. */
  onGoogleSignIn?: () => void | Promise<void>;
  /** Called when Microsoft sign-in button is clicked. When present, a second button is rendered under the Google one. */
  onMicrosoftSignIn?: () => void | Promise<void>;
  /** Shows loading spinner on button */
  isLoading?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Path to logo image (defaults to '/logo/elka-logo.png') */
  logoSrc?: string;
  /** Footer text (e.g., "© 2026 CRM אלקה | כל הזכויות שמורות") */
  footerText?: string;
  /** Custom content. Rendered below the Google button, or as the main content when onGoogleSignIn is omitted. */
  children?: ReactNode;
  /**
   * Calm-v2 split-screen layout: form side on surface (brand logo square,
   * solid-ink title, bordered Google button), brand side with the
   * brand→brandInk 135° gradient panel — the one place the brand gradient
   * lives. Only valid in apps that import `@elka/theme` v2/calm.css.
   * Default (false) keeps the legacy layout.
   */
  calm?: boolean;
  /** Calm only: small eyebrow line above the title (e.g. "אלקה · CRM"). */
  eyebrow?: string;
  /** Calm only: uppercase eyebrow on the brand panel (e.g. "ELKA MANAGER", "SALES OS"). */
  brandTitle?: string;
  /** Calm only: value-proposition lines on the brand panel (about 3 short lines). */
  valueProps?: string[];
  /** Calm only: helper line under the Google button (e.g. a Team Portal link for field workers). */
  helperText?: ReactNode;
  /** Calm only: square brand mark shown above the title (defaults to '/logo/elka-favicon.png'). */
  logoSquareSrc?: string;
}

function GoogleGlyph() {
  return (
    <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24" width="20" height="20">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

export function LoginPage({
  title,
  subtitle,
  onGoogleSignIn,
  onMicrosoftSignIn,
  isLoading = false,
  error,
  logoSrc = '/logo/elka-logo.png',
  footerText,
  children,
  calm = false,
  eyebrow,
  brandTitle,
  valueProps,
  helperText,
  logoSquareSrc = '/logo/elka-favicon.png',
}: LoginPageProps) {
  if (calm) {
    return (
      <div className="bg-surface flex min-h-screen">
        {/* Form side */}
        <div className="flex flex-[1.2] flex-col items-center justify-center px-6 py-10">
          <div className="w-full max-w-sm">
            <img
              src={logoSquareSrc}
              alt=""
              className="mb-6 h-11 w-11 rounded-[10px] object-contain"
            />
            {eyebrow && (
              <p className="text-muted mb-1 text-xs font-bold tracking-[1px] uppercase">
                {eyebrow}
              </p>
            )}
            <h1 className="text-ink text-[26px] font-extrabold tracking-[-0.3px] text-balance">
              {title}
            </h1>
            <p className="text-muted mt-1.5 mb-8 text-sm">{subtitle}</p>

            {error && (
              <div
                role="alert"
                className="rounded-control bg-danger-soft text-danger mb-5 px-4 py-3 text-sm font-semibold"
              >
                {error}
              </div>
            )}

            {onGoogleSignIn && (
              <button
                onClick={onGoogleSignIn}
                disabled={isLoading}
                className="rounded-control border-line-strong text-ink-2 hover:bg-surface-soft hover:border-brand flex min-h-[48px] w-full items-center justify-center gap-3 border bg-white px-4 py-3 text-sm font-bold transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <GoogleGlyph />
                    התחבר עם Google
                  </>
                )}
              </button>
            )}

            {helperText && (
              <div className="text-muted mt-5 text-xs leading-relaxed">{helperText}</div>
            )}

            {children}

            {footerText && <p className="text-sub mt-10 text-[11px]">{footerText}</p>}
          </div>
        </div>

        {/* Brand side — the one place the brand gradient lives */}
        <div
          className="hidden flex-1 flex-col justify-center px-12 md:flex"
          style={{ background: 'linear-gradient(135deg, #1D3A8A 0%, #0F2260 100%)' }}
        >
          {brandTitle && (
            <p className="mb-4 text-[11px] font-bold tracking-[2px] text-white/60 uppercase">
              {brandTitle}
            </p>
          )}
          {valueProps?.map((line) => (
            <p key={line} className="text-[22px] leading-snug font-bold text-white">
              {line}
            </p>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-pink-500 via-violet-500 to-cyan-500 px-4">
      {/* Animated background elements - Unicorn Rainbow */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -right-1/2 h-96 w-96 animate-pulse rounded-full bg-pink-300 opacity-20 blur-3xl"></div>
        <div
          className="absolute -bottom-1/2 -left-1/2 h-96 w-96 animate-pulse rounded-full bg-cyan-300 opacity-20 blur-3xl"
          style={{ animationDelay: '1s' }}
        ></div>
        <div
          className="absolute top-1/4 left-1/4 h-64 w-64 animate-pulse rounded-full bg-yellow-300 opacity-15 blur-3xl"
          style={{ animationDelay: '0.5s' }}
        ></div>
        <div
          className="absolute right-1/4 bottom-1/4 h-64 w-64 animate-pulse rounded-full bg-emerald-300 opacity-15 blur-3xl"
          style={{ animationDelay: '1.5s' }}
        ></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="glass-gradient border-opacity-20 rounded-3xl border border-white p-6 shadow-2xl backdrop-blur-xl md:p-10">
          <div className="mb-6 text-center md:mb-8">
            {/* Logo */}
            <div className="mx-auto mb-4 flex h-24 w-24 transform items-center justify-center rounded-2xl bg-white p-2 shadow-2xl transition-transform hover:scale-110 md:mb-6 md:h-32 md:w-32 md:p-3">
              <img src={logoSrc} alt="Logo" className="h-full w-full object-contain" />
            </div>

            {/* Title */}
            <h1 className="mb-2 text-3xl font-bold text-balance text-white drop-shadow-2xl md:mb-3 md:text-4xl">
              {title}
            </h1>

            {/* Subtitle */}
            <p className="text-base text-white drop-shadow-lg md:text-lg">{subtitle}</p>
          </div>

          {/* Error message */}
          {error && (
            <div
              role="alert"
              className="mb-6 animate-pulse rounded-xl border border-red-400/30 bg-red-500/20 p-4 text-sm text-red-200 backdrop-blur-sm"
            >
              {error}
            </div>
          )}

          {(onGoogleSignIn || onMicrosoftSignIn) && (
            <>
              {onGoogleSignIn && (
                <button
                  onClick={onGoogleSignIn}
                  disabled={isLoading}
                  className="flex min-h-[52px] w-full transform items-center justify-center gap-3 rounded-xl bg-white py-4 text-base text-gray-900 shadow-xl transition-[background-color,box-shadow,transform] hover:scale-105 hover:bg-gray-50 hover:shadow-2xl disabled:transform-none disabled:cursor-not-allowed disabled:opacity-50 md:text-lg"
                >
                  {isLoading ? (
                    <Spinner size="md" />
                  ) : (
                    <>
                      <svg
                        className="h-6 w-6 flex-shrink-0"
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                      >
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      התחבר עם Google
                    </>
                  )}
                </button>
              )}

              {onMicrosoftSignIn && (
                <button
                  onClick={onMicrosoftSignIn}
                  disabled={isLoading}
                  className={`flex min-h-[52px] w-full transform items-center justify-center gap-3 rounded-xl bg-white py-4 text-base text-gray-900 shadow-xl transition-[background-color,box-shadow,transform] hover:scale-105 hover:bg-gray-50 hover:shadow-2xl disabled:transform-none disabled:cursor-not-allowed disabled:opacity-50 md:text-lg ${
                    onGoogleSignIn ? 'mt-4' : ''
                  }`}
                >
                  {isLoading ? (
                    <Spinner size="md" />
                  ) : (
                    <>
                      <svg
                        className="h-5 w-5 flex-shrink-0"
                        viewBox="0 0 23 23"
                        width="20"
                        height="20"
                      >
                        <path fill="#f25022" d="M1 1h10v10H1z" />
                        <path fill="#00a4ef" d="M1 12h10v10H1z" />
                        <path fill="#7fba00" d="M12 1h10v10H12z" />
                        <path fill="#ffb900" d="M12 12h10v10H12z" />
                      </svg>
                      התחבר עם Microsoft
                    </>
                  )}
                </button>
              )}

              <div className="mt-8 text-center">
                <p className="text-sm text-white drop-shadow-md">נדרש חשבון מורשה</p>
              </div>
            </>
          )}

          {children}
        </div>

        {/* Footer */}
        {footerText && (
          <div className="mt-8 text-center">
            <p className="text-sm text-white opacity-75">{footerText}</p>
          </div>
        )}
      </div>
    </div>
  );
}
